import { useRef } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Stack } from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { ExpenseSummaryHeader } from '../components/ExpenseSummaryHeader';
import {
  expenseDetailsDefaultValues,
  ExpenseDetailsForm,
  expenseDetailsSchema,
  type ExpenseDetailsFormFields,
} from '../forms/ExpenseDetails';
import {
  ExpenseFileAttachments,
  type ExpenseFileAttachmentsHandle,
} from '../forms/ExpenseFileAttachments';
import {
  projectInformationDefaultValues,
  ProjectInformationForm,
  projectInformationSchema,
  type ProjectInformationFormFields,
} from '../forms/ProjectInformation';
import { useExpenseFormBlurredValuesStore } from '../stores/expenseFormBlurredValues';
import { RegisterExpenseFormData } from '../types';
import {
  calculateExchangeRate,
  generatePersonalExpenseRedirectUrl,
  generateSpecialCaseErrorMessage,
} from '../utils';
import { useCreatePersonalExpense } from '@/api/REST/personalExpenses';
import { useGetUserMe } from '@/api/REST/userMe';
import { useGetCurrencyCodeOptionByIsoCode } from '@/api/typesense/currencyCodes';
import { useGetCustomerOptionById } from '@/api/typesense/customers';
import { useGetEmployeeOptionById } from '@/api/typesense/employees';
import { useGetDefaultPaymentMethodOption } from '@/api/typesense/paymentMethods';
import { useGetProjectOptionById } from '@/api/typesense/projects';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAppForm } from '@/forms/useAppForm';

export const NewExpense = () => {
  const { t } = useTranslation('expenses');
  const formRef = useRef<HTMLFormElement>(null);
  const attachmentRef = useRef<ExpenseFileAttachmentsHandle>(null);

  const [searchParams] = useSearchParams();
  const employeeUserIdParam = searchParams.get('employeeUserId');
  const projectIdParam = searchParams.get('projectId');
  const dateParam = searchParams.get('date');

  const { data: userMeData, isLoading: isUserMeLoading } = useGetUserMe();

  const [attachedExpenseFiles, setAttachedExpenseFiles] = useState<string[]>(
    [],
  );
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const { reset: resetBlurredValueStore } = useExpenseFormBlurredValuesStore();

  useEffect(() => {
    resetBlurredValueStore();

    // Cleanup function to reset when component unmounts
    return () => {
      resetBlurredValueStore();
    };
  }, []);

  const initialDate = useMemo(() => {
    if (dateParam) {
      const parsed = parseISO(dateParam);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  }, [dateParam]);

  const employeeIdToFetch =
    employeeUserIdParam ?? userMeData?.userId ?? undefined;

  const { data: userCurrencySelectOption, isLoading: isUserCurrencyLoading } =
    useGetCurrencyCodeOptionByIsoCode(userMeData?.legalEntity.currencyCode);
  const { data: userEmployeeOption, isLoading: isUserEmployeeOptionLoading } =
    useGetEmployeeOptionById(employeeIdToFetch);
  const {
    data: preSelectedProjectOption,
    isLoading: isPreSelectedProjectOptionLoading,
  } = useGetProjectOptionById(projectIdParam ?? undefined);

  const {
    data: customerOptionOfPreselectedProject,
    isLoading: isCustomerOptionOfPreselectedProjectLoading,
  } = useGetCustomerOptionById(preSelectedProjectOption?.customerId);

  const { data: defaultPaymentMethod } =
    useGetDefaultPaymentMethodOption('personal');

  const createPersonalExpense = useCreatePersonalExpense();

  const form = useAppForm({
    defaultValues: {
      expense_details: {
        ...expenseDetailsDefaultValues,
        date: initialDate,
        expenseCurrency:
          !isUserCurrencyLoading && userCurrencySelectOption
            ? userCurrencySelectOption
            : null,
        paymentMethod: defaultPaymentMethod ?? null,
      },
      project_information: {
        ...projectInformationDefaultValues,
        employee:
          !isUserEmployeeOptionLoading && userEmployeeOption
            ? userEmployeeOption
            : null,
        ...(!isPreSelectedProjectOptionLoading &&
          preSelectedProjectOption && {
            project: preSelectedProjectOption,
          }),
        ...(!isCustomerOptionOfPreselectedProjectLoading &&
          customerOptionOfPreselectedProject && {
            customer: customerOptionOfPreselectedProject,
          }),
      },
    },
    validators: {
      onChange: z.object({
        expense_details: expenseDetailsSchema,
        project_information: projectInformationSchema,
      }),
    },
    onSubmit: async ({
      value,
      meta,
    }: {
      value: {
        expense_details: ExpenseDetailsFormFields;
        project_information: ProjectInformationFormFields;
      };
      meta?: { saveAndNew?: boolean };
    }) => {
      const payload = {
        expense_details: value.expense_details,
        project_information: value.project_information,
        // when targetCurrencyConvertedAmount is not set, we do not have a diverging currency, so exchange rate should calculate to 100
        exchangeRate: calculateExchangeRate(
          value.expense_details.amountIncludingVat,
          value.expense_details.targetCurrencyConvertedAmount
            ? value.expense_details.targetCurrencyConvertedAmount
            : value.expense_details.amountIncludingVat,
        ),
        legalEntityCurrencyIsoCode: selectedEmployeeCurrency
          ? selectedEmployeeCurrency.isoCode
          : '',
        attachedExpenseFiles: attachedExpenseFiles,
      } as RegisterExpenseFormData;

      await createPersonalExpense.mutateAsync(payload, {
        onSuccess: () => {
          if (meta && meta.saveAndNew) {
            const formDataToKeep = form.getFieldValue('project_information');
            form.reset();
            resetBlurredValueStore();
            attachmentRef.current?.reset();
            // Reset with new default values is not working as expected, so we manually set the values to keep
            form.setFieldValue('project_information', formDataToKeep);
          }
        },
      });
    },
    onSubmitInvalid: () => {
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  const handleSubmitAndNew = async () => {
    await form.handleSubmit({ saveAndNew: true });
  };

  const handleResetForm = () => {
    form.reset();
    resetBlurredValueStore();
    attachmentRef.current?.reset();
  };

  // we need this to set the billable checkbox based on the default value of the selected contract
  const selectedContract = useStore(
    form.store,
    (state) => state.values.project_information.contract,
  );

  useEffect(() => {
    if (selectedContract) {
      form.setFieldValue(
        'expense_details.billable',
        selectedContract.isBillable ?? false,
      );
    }
  }, [selectedContract]);

  const selectedEmployee = useStore(
    form.store,
    (state) => state.values.project_information.employee,
  );

  const {
    data: selectedEmployeeCurrency,
    isLoading: isLoadingSelectedEmployeeCurrency,
  } = useGetCurrencyCodeOptionByIsoCode(
    selectedEmployee?.legalEntityCurrencyIsoCode ?? undefined,
  );

  const successNavigationUrl = useMemo(() => {
    const params = {
      employeeUserId: form.state.values.project_information.employee?.id,
      projectId: form.state.values.project_information.project?.id,
      paymentMethodId: form.state.values.expense_details.paymentMethod?.id,
      date: form.state.values.expense_details.date,
    };
    return generatePersonalExpenseRedirectUrl(
      params,
      userMeData?.billingPlan.name === 'StarterPSA',
    );
  }, [form.state.values, userMeData]);

  const isSubmitDisabled =
    isUploadingFiles || isLoadingSelectedEmployeeCurrency || isUserMeLoading;

  return (
    <>
      <PageTitle title={t('new.PageTitle')} />
      <Panels autoSaveId="expense-panels">
        <Panels.Main panelContentProps={{ constrainWidth: true }}>
          <form.Subscribe selector={(state) => state.values}>
            {(val) => (
              <PageHeader
                title={t('new.PageTitle')}
                subtitle={t('new.PageSubtitle')}
              >
                <PageHeader.Info>
                  <ExpenseSummaryHeader
                    expenseCurrency={val.expense_details.expenseCurrency}
                    paymentMethod={val.expense_details.paymentMethod}
                    supplier={val.expense_details.supplier?.value}
                    category={val.expense_details.expenseType?.value}
                    selectedEmployee={val.project_information.employee}
                    expenseType="personal"
                  />
                </PageHeader.Info>
              </PageHeader>
            )}
          </form.Subscribe>
          <form.Form ref={formRef}>
            <Stack gap={{ xs: 2, sm: 4 }}>
              <ProjectInformationForm
                title={t('projectInformation.FormTitle')}
                fields="project_information"
                expenseType="personal"
                form={form}
              />

              <ExpenseDetailsForm
                title={t('expenseDetails.FormTitle')}
                fields="expense_details"
                form={form}
                expenseType="personal"
                expenseTargetCurrency={selectedEmployeeCurrency ?? null}
              />

              <form.SaveFormActions
                data-automation-id="new-expense-save-form-actions"
                form={form}
                formRef={formRef}
                isSubmitDisabled={isSubmitDisabled}
                successNavigationUrl={successNavigationUrl}
                onReset={handleResetForm}
                onSubmit={form.handleSubmit}
                onSubmitAndNew={handleSubmitAndNew}
                getErrorMessageFromError={generateSpecialCaseErrorMessage}
              />
            </Stack>
          </form.Form>
        </Panels.Main>
        <Panels.Secondary panelContentProps={{ hideOverflow: true }}>
          <ExpenseFileAttachments
            ref={attachmentRef}
            expenseType="personal"
            onFilesUploading={setIsUploadingFiles}
            onAttachedExpenseFilesChanged={setAttachedExpenseFiles}
          />
        </Panels.Secondary>
      </Panels>
    </>
  );
};
